<i18n>
{
	"en": {
		"map": "View map",
		"list": "View list",
		"ordersFound": "No orders | {count} order | {count} orders",
		"failedToAddOrderToTrip": "Failed to add order to trip {number}",
		"failedToAddOrderToTransporter": "Failed to add order to transporter",
		"orderAddedToTrip": "Order added to trip {number}",
		"orderAddedToTransporter": "Order added to transporter",
		"orderAlreadyAdded": "A selected order is already part of the selected trip.",
		"invalidMaxLoadingSequenceNo": "The existing orders on the trip have an invalid loading sequence. The corrupt data needs to be fixed first.",
		"validationErrors": "Trip is currently invalid, please edit trip before adding orders.",
		"showDeliveriesInMap": "Show deliveries",
		"tripsOnTop": "Display Trips on Top",
		"failedToFetch": "Failed to search for orders",
		"unableToUpdateTrip": "Could not update trip"
	},
	"no": {
		"map": "Kartvisning",
		"list": "Listevisning",
		"ordersFound": "Ingen oppdrag | {count} tilgjengelig oppdrag | {count} oppdrag",
		"failedToAddOrderToTrip": "Kunne ikke legge til oppdrag på turen {number}",
		"failedToAddOrderToTransporter": "Kunne ikke legge til oppdrag til transportøren",
		"orderAddedToTrip": "Oppdrag lagt til tur {number}",
		"orderAddedToTransporter": "Oppdrag lagt til transportøren",
		"orderAlreadyAdded": "Et valgt oppdrag er allerede en del av den valgte turen.",
		"invalidMaxLoadingSequenceNo": "De eksisterende oppdragene har ugyldig lastesekvens. Korrupt data må fikses først.",
		"validationErrors": "Turen er ugyldig. Rediger turen før du legger til oppdrag.",
		"showDeliveriesInMap": "Vis leveranser",
		"tripsOnTop": "Turer øverst",
		"failedToFetch": "Kunne ikke søke etter ordre",
		"unableToUpdateTrip": "Kunne ikke oppdatere tur"
	}
}
</i18n>
<template>
	<div class="loadplanning-map-grid" >
		<div class="map-body">
			<t-resizable-horizontal-panel
				:localStorageId="localStorageId"

				:disabled="!orderListVisible"
				:overflowX="orderListVisible ? 'hidden' : 'visible'"

				minRightPercentage="20"
				style="height:100%;width:100%"
			>
				<t-horizontal-panel v-if="!tripDetailsVisible" slot="left" :showRightPanel="true">
					<OrderSearchFilters
						slot="left"
						:filtersVisible="orderFiltersVisible"
						mapView
						@loadSavedSearch="$emit('loadSavedSearch',$event)"
						@saveSearch="toggleSaveSearch(true)"
						@toggleFilters="toggleOrderFilters(!orderFiltersVisible)"
					/>
					<template v-show="orderListVisible">
						<order-search-map-grid
							v-show="orderListVisible"
							:filtersVisible="orderFiltersVisible"
							@toggleFilters="toggleOrderFilters(!orderFiltersVisible)"
							@toggleOrderList="toggleOrderList(false)"
							@showAddToTrip="showAddToTrip"
							@showAddToTransporter="showAddToTransporter"
							@search="performFetchOrders"
						/>
					</template>
				</t-horizontal-panel>

				<t-horizontal-panel v-if="tripDetailsVisible" slot="left" :showRightPanel="true">
					<t-pane
						height="calc(100% - 48px)"
						border="default"
						background="tint2"
						borderRadius="5px"
						class="cargo-pane"
					>
						<trip-view-order-grid
							v-show="orderListVisible"
						/>
					</t-pane>
				</t-horizontal-panel>

				<t-horizontal-panel
					slot="right"
					:showRightPanel="true"
					style="position: relative; overflow: hidden; width:100%; margin-right:20px; box-shadow: 0 1px 6px 0 rgba(0, 0, 0, 0.2);"
				>

					<div v-show="!orderListVisible" class="order-list-header">
						<t-icon-button
							v-if="!orderFiltersVisible"
							icon="filter-open"
							color="blue"
							iconSize="sm"
							type="bp"
							style="margin-right: 5px"
							@click="toggleOrderFilters(!orderFiltersVisible)"
						/>
						<t-icon-button
							v-if="!orderFiltersVisible"
							icon="search"
							color="blue"
							iconSize="sm"
							type="bp"
							style="margin-right: 15px"
							@click="performFetchOrders"
						/>

						<t-panel-header icon="orders">
							{{ $tc('ordersFound', ordersFound)}}
						</t-panel-header>

						<div style="padding-left: 10px; cursor: pointer;" @click="toggleOrderList(true)">
							<t-timpex-icon icon="maximize" color="blue" type="bp" />
						</div>
					</div>
					<div class="map-settings">
						<t-checkbox
							:label="$t('tripsOnTop')"
							:checked="tripsOnTopInMap"
							@change="toggleTripsOnTop"
						/>
						<t-checkbox
							:label="$t('showDeliveriesInMap')"
							:checked="showDeliveriesInMap"
							@change="toggleShowDeliveries"
						/>
					</div>
					<!-- Add to map after some testing. @map:moveend="updateOrdersWithPolledOrders" -->
					<vue-map
						@showAddToTrip="showAddToTrip"
						@showAddToTransporter="showAddToTransporter"
					/>
					<TripSearchFilters
						v-show="tripFiltersVisible"
						slot="right"
						style="width:200px;flex-shrink:0"
						@toggleFilters="toggleTripFilters(!tripFiltersVisible)"
						@loadSavedSearch="$emit('loadSavedSearch',$event)"
						@saveSearch="toggleSaveSearch(true)"
					/>

				</t-horizontal-panel>

			</t-resizable-horizontal-panel>
		</div>
		<map-trip-search
			:filtersVisible="tripFiltersVisible"
			@toggleFilters="toggleTripFilters(!tripFiltersVisible)"
			@showAddToTrip="showAddToTrip"
			@showAddToTransporter="showAddToTransporter"
		/>
		<SaveSearch
			:isShown="saveSearchVisible"
			:orderFilter="orderFilter"
			:tripFilter="tripFilter"
			:loadedSearch="loadedSearch"
			@close="toggleSaveSearch(false)"
			@delete="onDeleteSavedSearch"
		/>
		<AddOrderToTripLocationsSelect
			:isShown="tripLocationsSelectVisible"
			:selectedTrip="selectedTrip"
			:orders="selectedOrders"
			:disableSubmit="addToTripProcessing"
			@close="toggleTripLocationsSelect(false)"
			@addToTrip="onAddOrdersToTrip"
		/>
		<AddOrderToTransporterSelect
			:isShown="transporterSelectVisible"
			:selectedTransporter="selectedTrip"
			:orders="selectedOrders"
			:disableSubmit="addToTransporterProcessing"
			@close="toggleTransporterSelect(false)"
			@addToTransporter="onAddOrdersToTransporter"
		/>
	</div>
</template>

<script>
import VueMap from './VueMap.vue';
import MapTripSearch from './MapTripSearch.vue';
import TripViewOrderGrid from './TripViewOrderGrid.vue';
import OrderSearchFilters from '../OrderSearch/OrderSearchFilters.vue';
import OrderSearchMapGrid from '../OrderSearch/OrderSearchMapGrid.vue';
import TripSearchFilters from '../TripSearch/TripSearchFilters';
import SaveSearch from '@/modules/saveLoadPlanningSearch/SaveSearch';
import AddOrderToTripLocationsSelect from '@/modules/loadPlanningList/modals/AddOrderToTripLocationsSelect';
import AddOrderToTransporterSelect from '@/modules/loadPlanningList/modals/AddOrderToTransporterSelect';
import {
	mapDynamicState, mapDynamicGetters, mapDynamicActions
} from '@/core/mapDynamic';
import { mapState as savedSearchState } from '@/modules/saveLoadPlanningSearch/store/saveLoadPlanningSearch.store.js';
import TResizableHorizontalPanel from '@/components/evergreen/timpex/layers/TResizableHorizontalPanel';

export default {
	name: 'MapMain',
	inject: ['namespace'],
	components: {
		TResizableHorizontalPanel,
		MapTripSearch,
		OrderSearchFilters,
		OrderSearchMapGrid,
		TripSearchFilters,
		VueMap,
		SaveSearch,
		AddOrderToTripLocationsSelect,
		AddOrderToTransporterSelect,
		TripViewOrderGrid
	},
	data() {
		return {
			mounting: true,
			selectedOrders: [],
			selectedTrip: null,
			saveSearchVisible: false,
			orderFiltersVisible: true,
			tripFiltersVisible: false,
			tripLocationsSelectVisible: false,
			transporterSelectVisible: false,
			orderListVisible: false,
			addToTripProcessing: false,
			addToTransporterProcessing: false,
		};
	},
	computed: {
		...mapDynamicState('computedStoreNamespace',[
			'loadedSearch',
			'showDeliveriesInMap',
			'tripsOnTopInMap',
			'tripDetailsVisible',
		]),
		...mapDynamicGetters('computedStoreNamespace',[
			'trips',
			'orderFilter',
			'tripFilter',
			'tripsLoading',
			'tripDetails',
			'filteredOrders'
		]),
		...mapDynamicState('computedOrderNamespace',['ordersLoading']),
		...savedSearchState(['savedSearches']),
		computedStoreNamespace() {
			return `loadPlanning/${this.namespace.id}`;
		},
		computedOrderNamespace() {
			return `loadPlanning/${this.namespace.id}/orderSearch`;
		},
		computedTripNamespace() {
			return `loadPlanning/${this.namespace.id}/tripSearch`;
		},
		localStorageId() {
			return `loadPlanningMap/${this.namespace.id}`;
		},
		savedSearch() {
			if (Array.isArray(this.savedSearches))
				return this.savedSearches.find(x => x.id === this.namespace.id);
			return {};
		},
		ordersFound() {
			return this.filteredOrders.length;
		},
	},
	watch: {
		'namespace.id': {
			handler() {
				this.setDefaultFilterVisibility();
			},
			immediate: true
		},
		savedSearch(newValue,oldValue) {
			if (this.mounting) return;
			if ( (oldValue === undefined && newValue !== undefined) ||
				(oldValue && newValue && oldValue.id !== newValue.id) ) {
				this.loadSavedAndSearch();
			} else if (newValue === undefined && this.namespace.id !== -1) {
				this.sendToDefaultSearch();
			}
		},
	},
	mounted() {
		if (this.namespace.tripId) {
			this.orderFiltersVisible = false;
			this.loadTripView(this.namespace.tripId);
		} else if (!this.savedSearch && this.namespace.id !== -1) {
			this.sendToDefaultSearch();
		} else if (this.savedSearch && this.namespace.id !== this.loadedSearch.id) {
			this.loadSavedAndSearch();
		} else if (this.namespace.id === this.loadedSearch.id) {
			this.fetchOrdersAndTrips();
		}
		this.startPollingTrips();
		//this.startPollingOrders(); TODO: Check if working on many orders.
		this.mounting = false;
	},
	destroyed() {
		this.stopPollingTrips();
		//this.stopPollingOrders();
	},
	methods: {
		...mapDynamicActions('computedStoreNamespace',[
			'setValue',
			'loadSavedSearch',
			'fetchOrdersAndTrips',
			'addOrdersToTrip',
			'addOrdersToTransporter',
			'loadTripView'
		]),
		...mapDynamicActions('computedOrderNamespace',[ 'fetchOrders', 'startPollingOrders', 'stopPollingOrders', 'updateOrdersWithPolledOrders' ]),
		...mapDynamicActions('computedTripNamespace',[ 'startPollingTrips', 'stopPollingTrips' ]),
		sendToDefaultSearch() {
			let defaultView = localStorage.getItem(`loadPlanning/-1/defaultView`);
			this.$router.replace({ name: defaultView === 'map' ? 'loadPlanningMap' : 'loadPlanningList', });
		},
		loadSavedAndSearch() {
			this.loadSavedSearch(this.savedSearch);
			this.fetchOrdersAndTrips();
		},
		setDefaultFilterVisibility() {
			let orderFilterOpen = localStorage.getItem(`${this.localStorageId}/orderFilterVisible`);
			if (orderFilterOpen === null || orderFilterOpen === 'true')
				this.toggleOrderFilters(true);
			else
				this.toggleOrderFilters(false);

			let tripFilterOpen = localStorage.getItem(`${this.localStorageId}/tripFilterVisible`);
			if (tripFilterOpen === 'true')
				this.toggleTripFilters(true);
			else
				this.toggleTripFilters(false);

			let orderListOpen = localStorage.getItem(`${this.localStorageId}/listVisible`);
			if (orderListOpen === null || orderListOpen === 'true')
				this.toggleOrderList(true);
			else
				this.toggleOrderList(false);

			let showDeliveries = localStorage.getItem(`${this.localStorageId}/showDeliveries`);
			if (showDeliveries === 'true')
				this.toggleShowDeliveries(true);

			let tripsOnTop = localStorage.getItem(`${this.localStorageId}/tripsOnTop`);
			if (tripsOnTop === 'true')
				this.toggleTripsOnTop(true);
		},
		toggleOrderFilters(value = true) {
			localStorage.setItem(`${this.localStorageId}/orderFilterVisible`, value);
			this.orderFiltersVisible = value;
		},
		toggleTripFilters(value = false) {
			localStorage.setItem(`${this.localStorageId}/tripFilterVisible`, value);
			this.tripFiltersVisible = value;
		},
		toggleOrderList(value = true) {
			this.orderListVisible = value;
			localStorage.setItem(`${this.localStorageId}/listVisible`, value);
		},
		toggleShowDeliveries(value = true) {
			this.setValue({ key: 'showDeliveriesInMap', value });
			localStorage.setItem(`${this.localStorageId}/showDeliveries`, value);
		},
		toggleTripsOnTop(value = true) {
			this.setValue({ key: 'tripsOnTopInMap', value });
			localStorage.setItem(`${this.localStorageId}/tripsOnTop`, value);
		},
		toggleSaveSearch(value) {
			this.saveSearchVisible = value;
		},
		onDeleteSavedSearch() {
			this.toggleSaveSearch(false);
			this.$router.push({ name: 'loadPlanningList' });
		},
		showAddToTrip({ trip, orders }) {
			// Trip is external transporter
			if (trip && trip.type.internalCode === 'external') {
				return;
			}
			this.selectedTrip = trip;
			this.selectedOrders = orders;
			this.toggleTripLocationsSelect(true);
		},
		showAddToTransporter({ trip, orders }) {
			this.selectedTrip = trip;
			this.selectedOrders = orders;
			this.toggleTransporterSelect(true);
		},
		toggleTripLocationsSelect(value) {
			this.tripLocationsSelectVisible = value;
		},
		toggleTransporterSelect(value) {
			this.transporterSelectVisible = value;
		},
		async onAddOrdersToTrip({ despatchOption, deliveryOption, etd, eta, customDespatchParty, customDeliveryParty }) {
			this.addToTripProcessing = true;
			try {
				await this.addOrdersToTrip({ orders: this.selectedOrders, tripId: this.selectedTrip.id, despatchOption, deliveryOption, etd, eta, customDespatchParty, customDeliveryParty });
				this.$notify({
					type: 'success',
					title: `${this.$t('orderAddedToTrip', { number: this.selectedTrip.tripNumber })}`
				});
				this.toggleTripLocationsSelect(false);
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddOrderToTrip'),
					duration: -1
				});
			}
			this.addToTripProcessing = false;
		},
		async onAddOrdersToTransporter({ vendor, etd, eta, despatchOption, deliveryOption, customDespatchParty, customDeliveryParty }) {
			this.addToTransporterProcessing = true;
			try {
				await this.addOrdersToTransporter({ orders: this.selectedOrders, vendor, etd, eta, despatchOption, deliveryOption, customDespatchParty, customDeliveryParty });
				this.$notify({
					type: 'success',
					title: `${this.$t('orderAddedToTransporter')}`
				});
				this.toggleTransporterSelect(false);
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t(e.message || 'failedToAddOrderToTransporter'),
					duration: -1
				});
			}
			this.addToTransporterProcessing = false;
		},
		async performFetchOrders() {
			try {
				await this.fetchOrders();
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToFetch')
				});
			}
		},
	}
};
</script>

<style lang="scss" scoped>

.order-list-header {
	position: fixed;
	display: flex;
	z-index: 999;
	background: white;
	padding: 0 15px;
	height: 50px;
	align-items: center;
}

.loadplanning-map-grid {
	display: grid;
	grid-template-rows: auto 182px;
	height: 100vh;
}
.map-body {
	display: flex;
	flex-flow: row;
	grid-row: 1;
	overflow: hidden;
	width: 100%;
	height: 100%;
}
.map-settings {
	position: absolute;
	display: grid;
	grid-auto-flow: column;
	column-gap: 10px;
	left: 0;
	bottom: 0;
	z-index: 999;
	background: rgba(255, 255, 255, 0.3);
	padding: 0 10px;
	height:32px;
}
</style>
